import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u1_x3_warmerueckgewinnung';

const M1_U1_X3_Warmerueckgewinnung: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();

  // Override laden
  const { override, refresh } = useUnitOverride(PAGE_ID);

  // Basistexte
  const base = {
    anlagenelemente:
      'Be- und Entlüftungsanlage mit Lufterhitzer und Kreuzstromwärmeaustauscher. Zu- und Abluftventilator, Außen- und Fortluftklappe, Zu- und Abluftfilter.',
    regelung:
      'Ablufttemperatur-Kaskadenregelung mit stetiger Minimal- und Maximalbegrenzung der Zulufttemperatur. Anfahrschaltung, Frostschutz luftseitig und wasserseitig, Stillstandsregelung, Vereisungsschutz, Pumpenblockierschutz.',
    funktionsbeschreibung:
      'Die Anlage wird durch Zeitprogramme eingeschaltet. Nach der Freigabe durch die Anfahrschaltung (siehe Zuluftregelung) wird die Regelung aktiviert. Der Fühler B3 erfasst die Ablufttemperatur als Führungsgröße, während der Fühler B2 die Zulufttemperatur als Regelgröße misst. Die Regelung versucht, die Zuluft so zu temperieren, dass die gewünschte Ablufttemperatur (Raumtemperatur) erreicht wird. Die Zulufttemperatur selbst wird dabei durch eine Minimal- und Maximalbegrenzung eingeschränkt, um Zugerscheinungen zu vermeiden.',
    wrg_funktion:
      'Die Regelung steuert zuerst die Wärmerückgewinnung Y2 und danach das Heizventil Y1 an. 1. WRG im Heizfall (Winterbetrieb): Die warme Abluft (z. B. 21°C) erwärmt die kalte Außenluft (z. B. 0°C) im Kreuzstromwärmetauscher. Die Außenluft wird vorgewärmt und das Heizventil deckt nur noch die Restdifferenz bis zum Sollwert (z. B. 20°C). 2. WRG im Kühlfall (Sommerbetrieb / Kälterückgewinnung): Die kühlere Abluft (z. B. 24°C) kühlt die warme Außenluft (z. B. 32°C) vor. Ein Kühlregister müsste dadurch weniger Leistung aufbringen.',
    schutzfunktionen:
      'Vereisungsschutz (B8): Fällt die Temperatur der Fortluft nach dem Wärmetauscher unter einen Grenzwert (z. B. 2°C), öffnet Y2 (Bypass) stetig zur Umströmung. Frostschutz (K9): Bei akuter Frostgefahr schaltet die Anlage ab, Klappen schließen, Heizventil Y1 öffnet voll zum Registerschutz. Stillstandsregelung & Pumpenblockierschutz: Sichern gegen Einfrieren und Festsitzen. Filterüberwachung K12, K13 sowie zentrale Steuerung aller Komponenten erfolgen im Schaltschrank.'
  };

  // Abgeleitete Textblöcke + Overrides
  const blocks = React.useMemo(() => ([
    { id: 'block_anlagenelemente', heading: 'Anlagenelemente', body: override?.texts?.['block_anlagenelemente'] ?? base.anlagenelemente },
    { id: 'block_regelung', heading: 'Regelung', body: override?.texts?.['block_regelung'] ?? base.regelung },
    { id: 'block_funktionsbeschreibung', heading: 'Funktionsbeschreibung', body: override?.texts?.['block_funktionsbeschreibung'] ?? base.funktionsbeschreibung },
    { id: 'block_wrg_funktion', heading: 'Funktionsweise der Wärmerückgewinnung (WRG)', body: override?.texts?.['block_wrg_funktion'] ?? base.wrg_funktion },
    { id: 'block_schutzfunktionen', heading: 'Schutzfunktionen', body: override?.texts?.['block_schutzfunktionen'] ?? base.schutzfunktionen }
  ]), [
    override,
    base.anlagenelemente,
    base.regelung,
    base.funktionsbeschreibung,
    base.wrg_funktion,
    base.schutzfunktionen
  ]);

  // TTS: Gesamten Text sprechen
  const fullPlainText = React.useMemo(() => blocks.map(b => `${b.heading}. ${b.body}`).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  // Admin: Bild ersetzen (ein Bild, Slot img_0)
  const handleImageReplace = React.useCallback(async (file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImagesM1) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, 'img_0', file, token);
      alert('Bild erfolgreich aktualisiert.');
      refresh();
    } catch (e: any) {
      console.error('Bild-Upload fehlgeschlagen:', e);
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImagesM1]);

  // Admin: Text speichern
  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTextsM1) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      console.error('Text-Speichern fehlgeschlagen:', e);
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTextsM1]);

  // TTS Button
  const ttsButton = (
    <IconButton onClick={speaking ? stop : speak} aria-label="Text vorlesen" sx={{ ml: 1 }}>
      <SpeakerIcon sx={{ fontSize: 28 }} />
    </IconButton>
  );

  // Bild-Quelle inkl. Override (Slot img_0, unified pattern)
  const imageSrc = (() => {
    const v = override?.images?.['img_0'];
    if (!v) return '/Bilder/schema_wrginrlt_trox.jpg';
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  })();

  return (
    <UnitPageShell title="1.1.3 Wärmerückgewinnung" actions={ttsButton}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Bilder-Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ZoomableImage
            src={imageSrc}
            alt="Schema Wärmerückgewinnung in RLT-Anlage"
            label="Bild einfügen"
            onReplace={canUploadImagesM1 ? handleImageReplace : undefined}
          />
        </Box>

        {/* Text-Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {blocks.map(b => (
            <EditableTextBlock
              key={b.id}
              pageId={PAGE_ID}
              blockId={b.id}
              heading={b.heading}
              body={b.body}
              onSave={(newBody: string) => handleTextSave(b.id, newBody)}
            />
          ))}

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/units/m1_u1_x99_quiz')}>
              Zum Quiz
            </Button>
          </Box>
        </Box>
      </Box>
    </UnitPageShell>
  );
};

export default M1_U1_X3_Warmerueckgewinnung;

